<template>
  <div 
    v-if="!isMinimized"
    :class="['dialer-dropdown', { 'is-dragging': isDragging, 'is-call-active': isCallActive }]"
    :style="dialerStyle"
    ref="dialerContainer"
  >
    <div class="dialer-container">
      <!-- Dialer Header (Draggable) -->
      <div 
        class="dialer-header"
        @mousedown="startDrag"
        @touchstart="startDrag"
      >
        <div class="header-left">
          <span class="drag-handle">⋮⋮</span>
          <h5>Phone Dialer</h5>
        </div>
        <div class="header-actions">
          <button class="minimize-btn" @click="minimizeDialer" title="Minimize">
            <span>−</span>
          </button>
          <button class="close-btn" @click="closeDialer" title="Close">
            <span>&times;</span>
          </button>
        </div>
      </div>

      <!-- Call Status Display -->
      <div class="call-status" v-if="callStatus">
        <div class="status-indicator" :class="callStatusClass">
          <span class="status-icon"></span>
          <span class="status-text">{{ callStatus }}</span>
        </div>
        <div class="call-duration" v-if="callDuration">{{ callDuration }}</div>
      </div>

      <!-- Phone Number Display with vue-tel-input -->
      <div class="phone-display">
        <vue-tel-input 
          v-model="phoneNumber" 
          @input="handlePhoneInput"
          @keyup.enter.native="makeCall"
          v-bind="{
            mode: 'international',
            defaultCountry: 'us',
            dynamicPlaceholder: true,
            inputId: 'dialer_phone',
            enabledCountryCode: false,
            maxLen: 15,
            validCharactersOnly: true,
            placeholder: 'Enter a phone number'
          }"
        />
      </div>

      <!-- Dialpad -->
      <div class="dialpad" v-show="!isCallActive">
        <div class="dialpad-row" v-for="(row, rowIndex) in dialpadButtons" :key="rowIndex">
          <button 
            v-for="button in row" 
            :key="button.value"
            @click="addDigit(button.value)"
            class="dialpad-button"
          >
            <span class="digit">{{ button.digit }}</span>
            <span class="letters" v-if="button.letters">{{ button.letters }}</span>
          </button>
        </div>
        
        <!-- Backspace Button -->
        <div class="dialpad-row">
          <button class="dialpad-button dialpad-action" @click="clearNumber">
            <span class="digit">Clear</span>
          </button>
          <button class="dialpad-button dialpad-empty"></button>
          <button class="dialpad-button dialpad-action" @click="backspace">
            <span class="digit">⌫</span>
          </button>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button 
          v-if="!isCallActive" 
          @click="makeCall" 
          class="call-btn"
          :disabled="!phoneNumber || isConnecting"
        >
          <img src="../assets/img/call-dail.svg" alt="Call" v-if="!isConnecting" />
          <span v-if="isConnecting">Connecting...</span>
          <span v-else>Call</span>
        </button>

        <button 
          v-if="isCallActive" 
          @click="endCall" 
          class="end-call-btn"
        >
          <span>End Call</span>
        </button>

        <button 
          v-if="isCallActive" 
          @click="toggleMute" 
          class="mute-btn"
          :class="{ active: isMuted }"
        >
          <span>{{ isMuted ? '🔇 Unmute' : '🔊 Mute' }}</span>
        </button>

        <button 
          v-if="isCallActive"
          @click="toggleKeypad"
          class="keypad-btn"
        >
          <span>{{ showKeypad ? 'Hide Keypad' : 'Show Keypad' }}</span>
        </button>
      </div>

      <!-- DTMF Keypad for Active Call -->
      <div class="dialpad dtmf-pad" v-show="isCallActive && showKeypad">
        <div class="dialpad-row" v-for="(row, rowIndex) in dialpadButtons" :key="'dtmf-' + rowIndex">
          <button 
            v-for="button in row" 
            :key="'dtmf-' + button.value"
            @click="sendDTMF(button.value)"
            class="dialpad-button"
          >
            <span class="digit">{{ button.digit }}</span>
            <span class="letters" v-if="button.letters">{{ button.letters }}</span>
          </button>
        </div>
      </div>

      <!-- Recent Calls -->
      <div class="recent-calls" v-if="recentCalls.length > 0 && !isCallActive">
        <h6>Recent Calls</h6>
        <ul>
          <li v-for="(call, index) in recentCalls" :key="index" @click="loadRecentNumber(call)">
            <span class="recent-number">{{ formatPhoneNumber(call) }}</span>
            <span class="recent-icon">↻</span>
          </li>
        </ul>
      </div>
    </div>
  </div>

  <!-- Minimized State -->
  <div 
    v-else
    class="dialer-minimized"
    @click.stop="restoreDialer"
    @mousedown="startDrag"
    @touchstart="startDrag"
    :style="minimizedStyle"
    ref="minimizedDialer"
  >
    <div class="minimized-content">
      <img src="../assets/img/phone.svg" alt="Phone" class="minimized-icon" />
      <span v-if="isCallActive" class="minimized-status">
        {{ callDuration || 'In Call' }}
      </span>
      <span v-else class="minimized-status">Dialer</span>
    </div>
  </div>
</template>

<script>
import { Device } from '@twilio/voice-sdk';
import axios from 'axios';

export default {
  name: 'Dialer',
  data() {
    return {
      phoneNumber: '',
      formattedNumber: '',
      device: null,
      currentCall: null,
      isCallActive: false,
      isConnecting: false,
      isMuted: false,
      callStatus: '',
      callDuration: '',
      callTimer: null,
      callStartTime: null,
      recentCalls: [],
      showKeypad: false,
      isMinimized: false,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dialerX: null,
      dialerY: null,
      initialPosition: { top: 70, right: 20 },
      dialpadButtons: [
        [
          { digit: '1', value: '1', letters: '' },
          { digit: '2', value: '2', letters: 'ABC' },
          { digit: '3', value: '3', letters: 'DEF' }
        ],
        [
          { digit: '4', value: '4', letters: 'GHI' },
          { digit: '5', value: '5', letters: 'JKL' },
          { digit: '6', value: '6', letters: 'MNO' }
        ],
        [
          { digit: '7', value: '7', letters: 'PQRS' },
          { digit: '8', value: '8', letters: 'TUV' },
          { digit: '9', value: '9', letters: 'WXYZ' }
        ],
        [
          { digit: '*', value: '*', letters: '' },
          { digit: '0', value: '0', letters: '+' },
          { digit: '#', value: '#', letters: '' }
        ]
      ]
    };
  },
  computed: {
    callStatusClass() {
      if (this.callStatus === 'Connected') return 'connected';
      if (this.callStatus === 'Ringing') return 'ringing';
      if (this.callStatus === 'Connecting') return 'connecting';
      return '';
    },
    dialerStyle() {
      if (this.dialerX !== null && this.dialerY !== null) {
        return {
          position: 'fixed',
          top: `${this.dialerY}px`,
          left: `${this.dialerX}px`,
          right: 'auto'
        };
      }
      return {
        position: 'fixed',
        top: `${this.initialPosition.top}px`,
        right: `${this.initialPosition.right}px`
      };
    },
    minimizedStyle() {
      if (this.dialerX !== null && this.dialerY !== null) {
        return {
          position: 'fixed',
          top: `${this.dialerY}px`,
          left: `${this.dialerX}px`
        };
      }
      return {
        position: 'fixed',
        bottom: '20px',
        right: '20px'
      };
    }
  },
  mounted() {
    this.initializeTwilioDevice();
    this.loadRecentCalls();
    // Set initial position based on screen size
    this.setInitialPosition();
  },
  beforeDestroy() {
    if (this.device) {
      this.device.destroy();
    }
    if (this.callTimer) {
      clearInterval(this.callTimer);
    }
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('touchmove', this.onDrag);
    document.removeEventListener('touchend', this.stopDrag);
  },
  methods: {
    setInitialPosition() {
      const screenWidth = window.innerWidth;
      if (screenWidth < 768) {
        // Mobile: center the dialer
        this.initialPosition = { top: 10, right: 10 };
      } else {
        this.initialPosition = { top: 70, right: 20 };
      }
    },

    formatPhoneNumber(phone) {
      if (!phone) return '';
      const cleaned = ('' + phone).replace(/\D/g, '');
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
      }
      return phone;
    },

    handlePhoneInput(formattedNumber, phoneObject) {
      if (phoneObject && phoneObject.formatted) {
        this.formattedNumber = phoneObject.formatted;
      }
    },

    addDigit(digit) {
      this.phoneNumber += digit;
    },

    sendDTMF(digit) {
      if (this.isCallActive && this.currentCall) {
        this.currentCall.sendDigits(digit);
        // Visual feedback
        this.$toast.open({
          position: 'top-right',
          message: `Sent: ${digit}`,
          type: 'info',
          duration: 1000
        });
      }
    },

    backspace() {
      if (this.phoneNumber && this.phoneNumber.length > 0) {
        this.phoneNumber = this.phoneNumber.slice(0, -1);
      }
    },

    clearNumber() {
      this.phoneNumber = '';
      this.formattedNumber = '';
    },

    loadRecentNumber(number) {
      this.phoneNumber = number;
    },

    toggleKeypad() {
      this.showKeypad = !this.showKeypad;
    },

    minimizeDialer() {
      this.isMinimized = true;
    },

    restoreDialer(event) {
      // Don't restore if we just finished dragging
      console.log(event);
      if (this.isDragging) {
        return;
      }
      this.isMinimized = false;
    },

    startDrag(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging = true;

      const touch = event.touches ? event.touches[0] : event;
      const rect = this.$refs.dialerContainer?.getBoundingClientRect() || this.$refs.minimizedDialer?.getBoundingClientRect();
      
      if (rect) {
        this.dragStartX = touch.clientX - rect.left;
        this.dragStartY = touch.clientY - rect.top;
      }

      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
      document.addEventListener('touchmove', this.onDrag);
      document.addEventListener('touchend', this.stopDrag);
    },

    onDrag(event) {
      if (!this.isDragging) return;

      const touch = event.touches ? event.touches[0] : event;
      let newX = touch.clientX - this.dragStartX;
      let newY = touch.clientY - this.dragStartY;

      // Boundary constraints
      const maxX = window.innerWidth - 320;
      const maxY = window.innerHeight - 100;

      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));

      this.dialerX = newX;
      this.dialerY = newY;
    },

    stopDrag(event) {
      if (this.isDragging) {
        event.stopPropagation();
      }
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
      document.removeEventListener('touchmove', this.onDrag);
      document.removeEventListener('touchend', this.stopDrag);
    },

    async initializeTwilioDevice() {
      try {
        const response = await axios.get('twilio/token', {
          headers: {
            Authorization: 'Bearer ' + this.$storage.get('auth').token
          }
        });

        if (response.data.token) {
          this.device = new Device(response.data.token, {
            logLevel: 1,
            codecPreferences: ['opus', 'pcmu']
          });

          this.device.on('registered', () => {
            console.log('Twilio Device Ready');
          });

          this.device.on('error', (error) => {
            console.error('Twilio Device Error:', error);
            this.callStatus = 'Error: ' + error.message;
            this.isConnecting = false;
            this.isCallActive = false;
          });

          this.device.on('incoming', (call) => {
            console.log('Incoming call from:', call.parameters.From);
            this.handleIncomingCall(call);
          });

          await this.device.register();
        }
      } catch (error) {
        console.error('Failed to initialize Twilio Device:', error);
        this.$toast.open({
          position: 'top-right',
          message: 'Failed to initialize dialer',
          type: 'error'
        });
      }
    },

    handleIncomingCall(call) {
      this.restoreDialer();
      this.currentCall = call;
      this.callStatus = 'Incoming Call';
      
      this.$confirm({
        title: 'Incoming Call',
        message: `Call from ${call.parameters.From}`,
        button: {
          no: 'Reject',
          yes: 'Answer'
        },
        callback: (confirm) => {
          if (confirm) {
            call.accept();
            this.setupCallHandlers();
          } else {
            call.reject();
            this.resetCallState();
          }
        }
      });
    },

    async makeCall() {
      if (!this.phoneNumber || this.isConnecting) return;

      try {
        this.isConnecting = true;
        this.callStatus = 'Connecting';

        // Use formattedNumber if available, otherwise phoneNumber
        const numberToCall = this.formattedNumber || this.phoneNumber;
        
        // currentCall = await makeCall({
        //             To: '+1'+CustomerPhoneNumber.value,
        //             // To: '+15595336954',
        //             dealerId: logged_in_dealer_id,
        //             chatId: DialpadCurrentChat.value?.id,
        //             userId: logged_in_user_id
        //         })

        const params = {
          To: numberToCall,
          dealerId : this.$storage.get('auth').user.dealer_id,
          userId : this.$storage.get('auth').user.id
        };

        this.currentCall = await this.device.connect({ params });
        this.setupCallHandlers();

      } catch (error) {
        console.error('Error making call:', error);
        this.$toast.open({
          position: 'top-right',
          message: 'Failed to make call: ' + error.message,
          type: 'error'
        });
        this.resetCallState();
      }
    },

    setupCallHandlers() {
      this.currentCall.on('accept', () => {
        console.log('Call accepted');
        this.isCallActive = true;
        this.isConnecting = false;
        this.callStatus = 'Connected';
        this.startCallTimer();
        this.addToRecentCalls(this.phoneNumber);
      });

      this.currentCall.on('disconnect', () => {
        console.log('Call ended');
        this.resetCallState();
      });

      this.currentCall.on('cancel', () => {
        console.log('Call cancelled');
        this.resetCallState();
      });

      this.currentCall.on('reject', () => {
        console.log('Call rejected');
        this.resetCallState();
      });

      this.currentCall.on('ringing', () => {
        this.callStatus = 'Ringing';
      });
    },

    endCall() {
      if (this.currentCall) {
        this.currentCall.disconnect();
      }
      this.resetCallState();
    },

    toggleMute() {
      if (this.currentCall) {
        this.isMuted = !this.isMuted;
        this.currentCall.mute(this.isMuted);
      }
    },

    startCallTimer() {
      this.callStartTime = Date.now();
      this.callTimer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - this.callStartTime) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        this.callDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }, 1000);
    },

    resetCallState() {
      this.isCallActive = false;
      this.isConnecting = false;
      this.isMuted = false;
      this.callStatus = '';
      this.callDuration = '';
      this.currentCall = null;
      this.showKeypad = false;
      
      if (this.callTimer) {
        clearInterval(this.callTimer);
        this.callTimer = null;
      }
    },

    addToRecentCalls(number) {
      if (number && !this.recentCalls.includes(number)) {
        this.recentCalls.unshift(number);
        if (this.recentCalls.length > 5) {
          this.recentCalls.pop();
        }
        this.saveRecentCalls();
      }
    },

    loadRecentCalls() {
      try {
        const saved = localStorage.getItem('dialer_recent_calls');
        if (saved) {
          this.recentCalls = JSON.parse(saved);
        }
      } catch (e) {
        console.error('Failed to load recent calls:', e);
      }
    },

    saveRecentCalls() {
      try {
        localStorage.setItem('dialer_recent_calls', JSON.stringify(this.recentCalls));
      } catch (e) {
        console.error('Failed to save recent calls:', e);
      }
    },

    closeDialer() {
      if (this.isCallActive) {
        this.$confirm({
          title: 'Call in Progress',
          message: 'Are you sure you want to close? The call will be ended.',
          button: {
            no: 'Cancel',
            yes: 'Close'
          },
          callback: (confirm) => {
            if (confirm) {
              this.endCall();
              this.$emit('close');
            }
          }
        });
      } else {
        this.$emit('close');
      }
    }
  }
};
</script>

<style scoped>
.dialer-dropdown {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  min-width: 320px;
  max-width: 380px;
  transition: box-shadow 0.2s;
  user-select: none;
}

.dialer-dropdown.is-dragging {
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.3);
  cursor: grabbing;
}

.dialer-dropdown.is-call-active {
  border: 2px solid #19cd9d;
}

.dialer-container {
  padding: 16px;
}

.dialer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 4px 12px;
  border-bottom: 2px solid #f0f0f0;
  cursor: grab;
  user-select: none;
}

.dialer-header:active {
  cursor: grabbing;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drag-handle {
  color: #999;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: -2px;
  cursor: grab;
}

.dialer-header h5 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 4px;
}

.minimize-btn,
.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  border-radius: 6px;
}

.minimize-btn:hover {
  background: #f0f0f0;
  color: #19cd9d;
}

.close-btn:hover {
  background: #fee;
  color: #dc3545;
}

.call-status {
  margin-bottom: 12px;
  text-align: center;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.status-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.status-indicator.connecting {
  background: #fff3cd;
  color: #856404;
}

.status-indicator.ringing {
  background: #cce5ff;
  color: #004085;
  animation: pulse 1s infinite;
}

.status-indicator.connected {
  background: #d4edda;
  color: #155724;
}

.status-icon {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { 
    opacity: 1; 
    transform: scale(1);
  }
  50% { 
    opacity: 0.5; 
    transform: scale(1.1);
  }
}

.call-duration {
  font-size: 24px;
  font-weight: 700;
  color: #19cd9d;
  font-variant-numeric: tabular-nums;
}

.phone-display {
  margin-bottom: 16px;
}

/* Vue Tel Input Overrides */
.phone-display >>> .vue-tel-input {
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  transition: all 0.2s;
  box-shadow: none;
}

.phone-display >>> .vue-tel-input:focus-within {
  border-color: #19cd9d;
  box-shadow: 0 0 0 3px rgba(25, 205, 157, 0.1);
}

.phone-display >>> .vti__input {
  padding: 14px 16px;
  font-size: 18px;
  border: none;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.phone-display >>> .vti__dropdown {
  padding: 10px;
  border: none;
  background: transparent;
}

.phone-display >>> .vti__dropdown:hover {
  background-color: #f8f9fa;
  border-radius: 8px;
}

.phone-display >>> .vti__dropdown-list {
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  max-height: 200px;
  z-index: 10001;
}

.phone-display >>> .vti__dropdown-item {
  padding: 10px 15px;
}

.phone-display >>> .vti__dropdown-item:hover {
  background-color: #f0f9f6;
}

.phone-display >>> .vti__dropdown-item.highlighted {
  background-color: #e8f7f2;
}

.dialpad {
  margin-bottom: 16px;
}

.dialpad.dtmf-pad {
  border-top: 2px solid #f0f0f0;
  padding-top: 16px;
  animation: slideDown 0.3s ease;
}

.dialpad-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.dialpad-button {
  flex: 1;
  padding: 16px 10px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f0f0f0 100%);
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.dialpad-button::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(25, 205, 157, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.3s, height 0.3s;
}

.dialpad-button:hover {
  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
  border-color: #19cd9d;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dialpad-button:active::before {
  width: 100px;
  height: 100px;
}

.dialpad-button:active {
  transform: scale(0.96);
}

.dialpad-button .digit {
  font-size: 26px;
  font-weight: 600;
  color: #333;
  line-height: 1;
}

.dialpad-button .letters {
  font-size: 9px;
  color: #999;
  margin-top: 3px;
  letter-spacing: 1.5px;
  font-weight: 500;
}

.dialpad-action {
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
  font-size: 14px;
  font-weight: 600;
}

.dialpad-action:hover {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.dialpad-empty {
  visibility: hidden;
  pointer-events: none;
}

.action-buttons {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.action-buttons button {
  flex: 1;
  min-width: 100px;
  padding: 14px 16px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  overflow: hidden;
}

.action-buttons button::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.5s, height 0.5s;
}

.action-buttons button:active::after {
  width: 200px;
  height: 200px;
}

.call-btn {
  background: linear-gradient(135deg, #19cd9d 0%, #17b88b 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(25, 205, 157, 0.3);
}

.call-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #17b88b 0%, #159978 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(25, 205, 157, 0.4);
}

.call-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
  opacity: 0.6;
}

.call-btn img {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
}

.end-call-btn {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
}

.end-call-btn:hover {
  background: linear-gradient(135deg, #c82333 0%, #bd2130 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(220, 53, 69, 0.4);
}

.mute-btn {
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

.mute-btn:hover {
  background: linear-gradient(135deg, #5a6268 0%, #545b62 100%);
  transform: translateY(-2px);
}

.mute-btn.active {
  background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
  color: #333;
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.4);
}

.keypad-btn {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.keypad-btn:hover {
  background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
  transform: translateY(-2px);
}

.recent-calls {
  border-top: 2px solid #f0f0f0;
  padding-top: 12px;
  animation: slideDown 0.3s ease;
}

.recent-calls h6 {
  margin: 0 0 8px 0;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.recent-calls ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.recent-calls li {
  padding: 10px 14px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid transparent;
}

.recent-calls li:hover {
  background: #e9ecef;
  border-color: #19cd9d;
  transform: translateX(4px);
}

.recent-number {
  font-weight: 500;
  color: #333;
}

.recent-icon {
  color: #19cd9d;
  font-size: 18px;
  font-weight: bold;
}

/* Minimized State */
.dialer-minimized {
  position: fixed;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #19cd9d 0%, #17b88b 100%);
  border-radius: 50%;
  box-shadow: 0 4px 16px rgba(25, 205, 157, 0.4);
  cursor: pointer;
  z-index: 10000;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: bounceIn 0.5s;
  user-select: none;
}

.dialer-minimized:active {
  cursor: grabbing;
}

@keyframes bounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.dialer-minimized:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(25, 205, 157, 0.5);
}

.minimized-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: white;
}

.minimized-icon {
  width: 24px;
  height: 24px;
  filter: brightness(0) invert(1);
}

.minimized-status {
  font-size: 10px;
  font-weight: 600;
  text-align: center;
  line-height: 1;
}

/* Responsive Design */
@media (max-width: 768px) {
  .dialer-dropdown {
    min-width: 280px;
    max-width: 95vw;
  }

  .dialer-container {
    padding: 12px;
  }

  .phone-input {
    font-size: 20px;
    padding: 12px;
  }

  .dialpad-button {
    padding: 14px 8px;
  }

  .dialpad-button .digit {
    font-size: 24px;
  }

  .dialpad-button .letters {
    font-size: 8px;
  }

  .action-buttons button {
    font-size: 14px;
    padding: 12px 14px;
  }

  .dialer-minimized {
    width: 56px;
    height: 56px;
  }
}

@media (max-width: 480px) {
  .dialer-dropdown {
    min-width: 260px;
  }

  .phone-input {
    font-size: 18px;
  }

  .dialpad-button {
    padding: 12px 6px;
  }

  .dialpad-button .digit {
    font-size: 22px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .action-buttons button {
    width: 100%;
  }
}

/* Touch Device Optimizations */
@media (hover: none) and (pointer: coarse) {
  .dialpad-button {
    padding: 18px 10px;
  }

  .action-buttons button {
    padding: 16px 18px;
    min-height: 50px;
  }
}
</style>

